/**
 * Konkord: how well this network makes direct (P2P) connections, which
 * streams on our voice server depend on.
 *
 * Two public STUN servers tell which public address and port they see us
 * from. The same port for both (Chrome then reports a single candidate):
 * the router keeps one mapping per local port ("open" NAT), direct links
 * work with nearly everyone. Different ports (two candidates): a
 * symmetric NAT (common on mobile data and some carriers' shared IPv4),
 * which fails against another closed network. No answer at all: UDP is
 * blocked (some offices and schools). An IPv6 address needs no NAT, so it
 * connects directly to anyone else on IPv6.
 */

const STUN_SERVERS = [
  "stun:stun.l.google.com:19302",
  "stun:stun.cloudflare.com:3478",
];

const GATHER_TIMEOUT_MS = 6000;

export type NetworkVerdict = "great" | "good" | "limited" | "blocked";

export type NetworkResult = {
  verdict: NetworkVerdict;
  /** A public IPv6 address answered */
  ipv6: boolean;
  /** IPv4 behind an open NAT, a symmetric one, or unknown (one server) */
  ipv4: "open" | "symmetric" | "unknown" | "none";
};

type Mapped = { address: string; port: number };

/**
 * The mapped addresses each STUN server reports, then the verdict
 */
export async function testNetwork(): Promise<NetworkResult> {
  const mapped: Mapped[] = [];
  let serverErrors = 0;
  const pc = new RTCPeerConnection({
    iceServers: STUN_SERVERS.map((urls) => ({ urls })),
  });
  pc.createDataChannel("konkord-network-test");
  // a STUN server that didn't answer: one candidate then proves nothing
  pc.onicecandidateerror = () => serverErrors++;
  try {
    await new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, GATHER_TIMEOUT_MS);
      pc.onicecandidate = (event) => {
        if (!event.candidate) {
          clearTimeout(timer);
          resolve();
          return;
        }
        const candidate = event.candidate;
        if (candidate.type !== "srflx" || !candidate.address) return;
        mapped.push({
          address: candidate.address,
          port: candidate.port ?? 0,
        });
      };
      void pc
        .createOffer()
        .then((offer) => pc.setLocalDescription(offer))
        .catch(() => resolve());
    });
  } finally {
    pc.close();
  }
  return judge(mapped, serverErrors > 0);
}

/**
 * Konkord: the verdict from what the STUN servers saw (kept apart to be
 * tested)
 */
export function judge(mapped: Mapped[], serverFailed = false): NetworkResult {
  const ipv6 = mapped.some((m) => m.address.includes(":"));
  const v4 = mapped.filter((m) => !m.address.includes(":"));

  let ipv4: NetworkResult["ipv4"] = "none";
  if (v4.length) {
    const ports = new Set(v4.map((m) => `${m.address}:${m.port}`));
    // a mapping per server: symmetric; one for both (unless one server
    // failed, then we can't tell)
    ipv4 = ports.size > 1 ? "symmetric" : serverFailed ? "unknown" : "open";
  }

  const verdict: NetworkVerdict = !mapped.length
    ? "blocked"
    : ipv6 || ipv4 === "open"
      ? "great"
      : ipv4 === "unknown"
        ? "good"
        : "limited";
  return { verdict, ipv6, ipv4 };
}
